import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Plus, Pencil, Trash2, Heart, Shield, Users, Info, Globe, HandHeart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const iconOptions = [
  { value: 'Heart', label: 'Coeur', icon: Heart },
  { value: 'Shield', label: 'Bouclier', icon: Shield },
  { value: 'HandHeart', label: 'Main Coeur', icon: HandHeart },
  { value: 'Users', label: 'Utilisateurs', icon: Users },
  { value: 'Info', label: 'Info', icon: Info },
  { value: 'Globe', label: 'Globe', icon: Globe },
];

const AdminValuesPage = () => {
  const [values, setValues] = useState([]);
  const [editingValue, setEditingValue] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    icon: 'Heart',
    title: '',
    description: ''
  });

  useEffect(() => {
    fetchValues();
  }, []);

  const fetchValues = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/values`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      setValues(response.data.values || []);
    } catch (error) {
      console.error('Error fetching values:', error);
    }
  };

  const handleSubmit = async () => {
    const auth = localStorage.getItem('admin_auth');
    try {
      if (editingValue) {
        await axios.put(`${BACKEND_URL}/api/admin/values/${editingValue.id}`, formData, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success('Valeur mise à jour!');
      } else {
        const newValue = { ...formData, id: `value-${Date.now()}` };
        await axios.post(`${BACKEND_URL}/api/admin/values`, newValue, {
          headers: { Authorization: `Basic ${auth}` }
        });
        toast.success('Valeur ajoutée!');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchValues();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (valueId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette valeur?')) return;
    const auth = localStorage.getItem('admin_auth');
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/values/${valueId}`, {
        headers: { Authorization: `Basic ${auth}` }
      });
      toast.success('Valeur supprimée!');
      fetchValues();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openEditDialog = (value) => {
    setEditingValue(value);
    setFormData(value);
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingValue(null);
    setFormData({
      id: '',
      icon: 'Heart',
      title: '',
      description: ''
    });
  };

  const getIconComponent = (iconName) => {
    const iconOption = iconOptions.find(opt => opt.value === iconName);
    return iconOption ? iconOption.icon : Heart;
  };

  return (
    <div data-testid="admin-values-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-2xl text-[#F5F0E8] mb-1">Valeurs & Idéologie</h1>
          <p className="text-sm text-[#B9B2A8]">Gérez les valeurs affichées sur la page Idéologie</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0B0B0C] border-white/10">
            <DialogHeader>
              <DialogTitle className="text-[#F5F0E8] font-display">
                {editingValue ? 'Modifier la valeur' : 'Nouvelle valeur'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Icône</label>
                <Select value={formData.icon} onValueChange={(v) => setFormData({ ...formData, icon: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-[#F5F0E8]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B0B0C] border-white/10">
                    {iconOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className="text-[#F5F0E8]">
                        <span className="flex items-center gap-2">
                          <opt.icon className="w-4 h-4" />
                          {opt.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Titre</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Respect"
                  className="bg-white/5 border-white/10 text-[#F5F0E8]"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-[#B9B2A8] mb-2">Description</label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="bg-white/5 border-white/10 text-[#F5F0E8] resize-none"
                />
              </div>
              <Button onClick={handleSubmit} className="w-full bg-[#C9A84C] text-[#060606] hover:bg-[#E2C56A]">
                {editingValue ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {values.map((value, index) => {
          const IconComponent = getIconComponent(value.icon);
          return (
            <motion.div
              key={value.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white/5 border border-white/10 rounded-xl p-5"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#C9A84C]/20 flex items-center justify-center flex-shrink-0">
                  <IconComponent className="w-5 h-5 text-[#C9A84C]" />
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg text-[#F5F0E8] mb-1">{value.title}</h3>
                  <p className="text-sm text-[#B9B2A8]">{value.description}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => openEditDialog(value)} className="h-8 w-8 text-[#B9B2A8] hover:text-[#F5F0E8]">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(value.id)} className="h-8 w-8 text-[#B9B2A8] hover:text-red-400">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminValuesPage;
